//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import * as collectionsApi from '@apis/collections/collectionsApi';
import { CollectionType } from '@apis/collections/CollectionType';
import { ICollectionFiltersDto } from '@apis/collections/ICollectionFiltersDto';
import { ICollectionSourcesDto } from '@apis/collections/ICollectionSourcesDto';
import * as gatewayInstagramApi from '@apis/gateway-instagram/gatewayInstagramApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { ICuralateStore } from '@models/AppState';
import { closeStreamModal, setSelectedStream, setStreams } from '@streams-drawer/actions/streams-drawer-actions';
import '@streams-drawer/actions/streams-drawer-actions';
import { IStream } from '@streams-drawer/model/IStream';
import { ASSET_FEEDS_MODAL_ACTIONS, DEFAULT_FILTERS, generateDuplicateExclusionFilter, MEDIA_SOURCE, UGC_SOURCE, UPLOADED_SOURCE } from '@streams/constants/assetFeedsModalConstants';
import { IFilters } from '@streams/models/assetFeedsModalStateModel';
import { getOldInstagramSource } from '@streams/selectors/getOldInstagramSource';
import { sidebarFiltersToAssetFeedCollectionFilters } from '@streams/streamsUtils';
import { taggingFeedActions } from '@tagging/actions/taggingFeedActions';
import { createAction } from '@utils/actions';
import { ReduxDispatch } from 'app/curalate.store';

function setAssetFeedsStreamSource(mediaSource: MEDIA_SOURCE, value: boolean) {
  return createAction({
    type: ASSET_FEEDS_MODAL_ACTIONS.SET_ASSET_FEEDS_STREAM_SOURCE,
    mediaSource,
    value,
  });
}

function setAssetFeedSourceConnectionState(mediaSource: MEDIA_SOURCE, value: boolean) {
  return createAction({
    type: ASSET_FEEDS_MODAL_ACTIONS.SET_ASSET_FEEDS_SOURCE_CONNECTION_STATE,
    mediaSource,
    value,
  });
}

function setUpdatingFilters(isUpdatingFilters: boolean) {
  return createAction({
    type: ASSET_FEEDS_MODAL_ACTIONS.SET_UPDATING_FILTERS,
    isUpdatingFilters
  });
}

function setUpdatingSources(isUpdatingSources: boolean) {
  return createAction({
    type: ASSET_FEEDS_MODAL_ACTIONS.SET_UPDATING_SOURCES,
    isUpdatingSources
  });
}

function setInstagramBusinessUserId(instagramBusinessId: string) {
  return createAction({
    type: ASSET_FEEDS_MODAL_ACTIONS.SET_INSTAGRAM_BUSINESS_USER_ID,
    instagramBusinessId
  });
}

function setEditingSources(isEditingSources: boolean) {
  return createAction({
    type: ASSET_FEEDS_MODAL_ACTIONS.SET_EDITING_SOURCES,
    isEditingSources
  });
}

export type AssetFeedsModalActionType = ReturnType<
  typeof setAssetFeedsStreamSource |
  typeof setAssetFeedSourceConnectionState |
  typeof setUpdatingFilters |
  typeof setUpdatingSources |
  typeof setInstagramBusinessUserId |
  typeof setEditingSources
>;

export const assetFeedsModalActions = {
  setAssetFeedSourceConnectionState,

  changeAssetFeedsStreamSource: (mediaSource: MEDIA_SOURCE, value: boolean) => (
    dispatch: ReduxDispatch
  ) => {
    dispatch(setEditingSources(true));
    dispatch(setAssetFeedsStreamSource(mediaSource, value));
  },

  closeAssetFeedsModal: () => (dispatch: ReduxDispatch) => {
    dispatch(closeStreamModal());
  },

  updateAssetFeedStreamFilters: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore
  ) => {
    dispatch(setUpdatingFilters(true));

    const filters = getState().streams.streamsDrawer.filters as IFilters;
    const assetFeedsStream = getState().streams.streamsDrawer.selectedStream;

    const updatedFilters: ICollectionFiltersDto = sidebarFiltersToAssetFeedCollectionFilters(filters);

    const updatedAssetFeedsStream = await collectionsApi.updateStream(
      assetFeedsStream.id,
      assetFeedsStream.name,
      assetFeedsStream.sources,
      updatedFilters,
    );

    dispatch(setSelectedStream(updatedAssetFeedsStream as IStream));
    dispatch(setUpdatingFilters(false));

    dispatch(toastNotificationsActions.addToastNotification(
      {
        title: 'Success!',
        message: 'Stream filters have been updated.',
        type: ToastType.SUCCESS,
      }
    ));
  },

  createOrUpdateAssetFeedsStream: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const streams = getState().streams.streamsDrawer.streams;
    const hasAssetFeedStream = streams.length > 0;

    dispatch(setUpdatingSources(true));

    if (hasAssetFeedStream) {
      await dispatch(updateAssetFeedsStream());
    } else {
      await dispatch(createAssetFeedsStream());
    }

    dispatch(setUpdatingSources(false));
    dispatch(setEditingSources(false));
  },

  setAssetFeedsStreamSources: (stream: IStream) => (dispatch: ReduxDispatch) => {
    if (stream.sources.allCuralateUsers) {
      dispatch(setAssetFeedsStreamSource(MEDIA_SOURCE.UPLOADED, true));
    }

    const ugcAndBrandInstagramEnabled = stream.sources.allInstagram && !stream.filters.excludedContentSources;
    if (stream.sources.instagramBusinessUsers.length > 0 || ugcAndBrandInstagramEnabled) {
      dispatch(setAssetFeedsStreamSource(MEDIA_SOURCE.INSTAGRAM, true));
    }

    if (stream.sources.allInstagram) {
      dispatch(setAssetFeedsStreamSource(MEDIA_SOURCE.UGC, true));
    }
  },

  cancelUpdate: (selectedStream: IStream) => (dispatch: ReduxDispatch) => {
    dispatch(assetFeedsModalActions.closeAssetFeedsModal());
    dispatch(assetFeedsModalActions.setAssetFeedsStreamSources(selectedStream)); // Reset toggles to state of stream
    dispatch(setEditingSources(false));
  }
};

const generateNewInstagramSource = () => async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
) => {
  const sourceConnectionStates = getState().streams.assetFeedsModal.sourceConnectionStates;

  if (sourceConnectionStates.instagram) {
    const brandInstagramUsername = getState().streams.streamsDrawer.brandInstagramUsername;
    let instagramBusinessId = getState().streams.assetFeedsModal.instagramBusinessId;

    if (!instagramBusinessId) {
      const instagramData = await gatewayInstagramApi.getBusinessUserForClient(brandInstagramUsername);

      dispatch(setInstagramBusinessUserId(instagramData.instagramBusinessId));
      instagramBusinessId = instagramData.instagramBusinessId;
    }

    return {
      instagramBusinessUsers: [instagramBusinessId],
      businessUserMappings: {
        [instagramBusinessId]: brandInstagramUsername,
      },
    };
  } else {
    return {};
  }
};

const generateUpdatedFilters = () => (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
): ICollectionFiltersDto  => {
  const sourceSettings = getState().streams.assetFeedsModal.sourceSettings;
  const brandInstagramUsername = getState().streams.streamsDrawer.brandInstagramUsername;

  const rights = {
    options: [
      'Owned',
      ...(sourceSettings.ugc ? ['Approved'] : []),
    ],
  };

  const shouldAddBrandExclusion = !sourceSettings.instagram && sourceSettings.ugc;
  const excludedContentSources = shouldAddBrandExclusion ? [{
    network: 'Instagram',
    contentType: 'Username',
    value: brandInstagramUsername,
  }] : [];

  const duplicateItemExclusions = generateDuplicateExclusionFilter(sourceSettings);

  return {
    rights,
    excludedContentSources,
    duplicateItemExclusions,
  };

};

const updateAssetFeedsStream = () => async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
) => {
  const assetFeedsStream = getState().streams.streamsDrawer.selectedStream;
  const { sourceConnectionStates, sourceSettings } = getState().streams.assetFeedsModal;

  const instagramSourceToSave: ICollectionSourcesDto = sourceConnectionStates.instagram
    ? await dispatch(generateNewInstagramSource())
    : getOldInstagramSource(getState());

  const sources: ICollectionSourcesDto = {
    ...(sourceSettings.instagram && instagramSourceToSave),
    ...(sourceSettings.uploaded && UPLOADED_SOURCE),
    ...(sourceSettings.ugc && UGC_SOURCE)
  };

  const updatedFilters = dispatch(generateUpdatedFilters());

  const filters = {
    ...assetFeedsStream.filters,
    ...updatedFilters,
  };

  const updatedAssetFeedsStream = await collectionsApi.updateStream(
    assetFeedsStream.id,
    assetFeedsStream.name,
    sources,
    filters,
  );

  dispatch(setSelectedStream(updatedAssetFeedsStream as IStream));

  dispatch(assetFeedsModalActions.closeAssetFeedsModal());

  dispatch(toastNotificationsActions.addToastNotification(
    {
      title: 'Success!',
      message: 'Stream sources have been updated.',
      type: ToastType.SUCCESS,
    }
  ));

  await dispatch(taggingFeedActions.generateFeed());

  dispatch(taggingFeedActions.refreshFeed());
};

const createAssetFeedsStream = () => async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
) => {
  const sourceSettings = getState().streams.assetFeedsModal.sourceSettings;

  const updatedFilters = dispatch(generateUpdatedFilters());
  const newInstagramSource: ICollectionSourcesDto = await dispatch(generateNewInstagramSource());

  const sources: ICollectionSourcesDto = {
    ...(sourceSettings.instagram && newInstagramSource),
    ...(sourceSettings.uploaded && UPLOADED_SOURCE),
    ...(sourceSettings.ugc && UGC_SOURCE)
  };

  const filters = {
    ...DEFAULT_FILTERS,
    ...updatedFilters,
  };

  const assetFeedsStream = await collectionsApi.createStream(
    'Google Asset Feed Stream',
    sources,
    filters,
    CollectionType.GOOGLE_FEED_STREAM,
  ) as IStream;

  dispatch(setStreams([assetFeedsStream] as IStream[]));
};
